/*
 * TrialRibbon
 *
 * A slim, always-visible disclosure ribbon that sits at the top of every
 * authenticated page. Its purpose is legal/UX clarity: users on the free
 * trial must ALWAYS see, without dismissing anything, that they are on a
 * trial that converts to a paid subscription. This prevents any accusation
 * of "false advertisement" or surprise billing after install.
 *
 * States:
 *   1. Active paid subscriber   → subtle "Selah Premium · $7.77/mo · Manage" (charcoal)
 *   2. Free trial in progress   → rose "Day X of 7 · Free trial · $7.77/mo after · Cancel"
 *   3. Trial ended, no sub      → amber "Trial ended — Subscribe to continue"
 *   4. No user / loading        → null
 *
 * Design:
 *   • Slim (32-36px tall), non-dismissable by design.
 *   • Text is a link to /settings so any tap takes them to full billing details.
 *   • Uses Selah CSS variables so it stays theme-consistent (light + dark).
 *   • Never covers content — mounted at the very top of the page column.
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Clock, ShieldCheck, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { getDisplayPriceUSD } from '../utils/platform';

const TrialRibbon = () => {
  const { user } = useAuth() || {};
  const [status, setStatus] = useState(null);
  const location = useLocation();
  // Force re-render when location changes to pick up platform detection
  const [displayPrice, setDisplayPrice] = useState(getDisplayPriceUSD());

  useEffect(() => {
    // Update display price when location changes (e.g., ?_platform=ios param)
    setDisplayPrice(getDisplayPriceUSD());
  }, [location.search]);

  useEffect(() => {
    if (!user?.id) {
      setStatus(null);
      return undefined;
    }
    let cancelled = false;
    api
      .get('/api/subscription/status')
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        /* Silent — the ribbon is a disclosure, not a critical UI path. */
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Apple App Review Guideline 3.1.1 note (historical):
  //   Previously we hid this ribbon on iOS entirely (the "reader-rule" era)
  //   because it linked out to Stripe. That path is deprecated. iOS builds
  //   now use Apple StoreKit 2 IAP (see components/Paywall.jsx +
  //   utils/iapBridge.js), so the "Subscribe" CTA below routes through
  //   Apple's official purchase sheet on iOS and through Stripe on web.
  //   Ribbon renders on every platform.
  // NOTE: hook order must be preserved — the render guard below runs after
  // useEffect above so React's Rules of Hooks stay satisfied.

  if (!user?.id || !status) return null;

  const isActive = status.subscription?.is_active;
  const isTrial = status.trial?.is_trial && status.days_remaining > 0;
  const isExpired = status.needs_subscription;

  // Platform-aware price string. On iOS Apple's catalog price is $7.79
  // (closest available tier to our $7.77 covenant); on web/other stores
  // Stripe still charges the original $7.77.
  const priceStr = `$${displayPrice.toFixed(2)}`;

  // Nothing to show if none of the three flags are meaningful
  if (!isActive && !isTrial && !isExpired) return null;

  // ------------- Shared ribbon container styles -------------
  const baseStyle = {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '12.5px',
    fontWeight: 500,
    letterSpacing: '0.005em',
    lineHeight: 1.35,
  };

  // ------------- STATE 1: Active paid subscriber -------------
  if (isActive) {
    return (
      <Link
        to="/settings"
        data-testid="trial-ribbon-premium"
        style={{
          ...baseStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '7px 14px',
          background:
            'linear-gradient(90deg, rgba(184,120,132,0.10) 0%, rgba(184,120,132,0.16) 50%, rgba(184,120,132,0.10) 100%)',
          borderBottom: '1px solid rgba(184,120,132,0.22)',
          color: 'var(--sd-fg)',
          textDecoration: 'none',
          textAlign: 'center',
        }}
      >
        <ShieldCheck
          size={13}
          strokeWidth={2.2}
          style={{ color: 'var(--sd-primary)', flexShrink: 0 }}
        />
        <span>
          <strong style={{ color: 'var(--sd-primary-dark)' }}>Selah Premium</strong>
          <span style={{ opacity: 0.55, margin: '0 6px' }}>·</span>
          {priceStr}/month
          <span style={{ opacity: 0.55, margin: '0 6px' }}>·</span>
          <span style={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}>
            Manage
          </span>
        </span>
      </Link>
    );
  }

  // ------------- STATE 2: Active free trial -------------
  if (isTrial) {
    const dayOf = Math.max(1, Math.min(7, 7 - status.days_remaining + 1));
    return (
      <Link
        to="/settings"
        data-testid="trial-ribbon-active"
        style={{
          ...baseStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '7px 14px',
          background:
            'linear-gradient(90deg, rgba(184,120,132,0.22) 0%, rgba(215,161,168,0.28) 50%, rgba(184,120,132,0.22) 100%)',
          borderBottom: '1px solid rgba(184,120,132,0.35)',
          color: 'var(--sd-fg)',
          textDecoration: 'none',
          textAlign: 'center',
        }}
      >
        <Clock
          size={13}
          strokeWidth={2.2}
          style={{ color: 'var(--sd-primary-dark)', flexShrink: 0 }}
        />
        <span>
          <strong style={{ color: 'var(--sd-primary-dark)' }}>
            Day {dayOf} of 7
          </strong>
          <span style={{ opacity: 0.55, margin: '0 6px' }}>·</span>
          <span className="hidden sm:inline">Free trial</span>
          <span className="sm:hidden">Trial</span>
          <span style={{ opacity: 0.55, margin: '0 6px' }}>·</span>
          <span className="hidden sm:inline">{priceStr}/month after</span>
          <span className="sm:hidden">{priceStr}/mo after</span>
          <span style={{ opacity: 0.55, margin: '0 6px' }}>·</span>
          <span style={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}>
            Cancel anytime
          </span>
        </span>
      </Link>
    );
  }

  // ------------- STATE 3: Trial expired, no subscription -------------
  if (isExpired) {
    return (
      <Link
        to="/settings"
        data-testid="trial-ribbon-expired"
        style={{
          ...baseStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '7px 14px',
          background:
            'linear-gradient(90deg, rgba(184,90,90,0.22) 0%, rgba(210,110,110,0.28) 50%, rgba(184,90,90,0.22) 100%)',
          borderBottom: '1px solid rgba(184,90,90,0.42)',
          color: 'var(--sd-fg)',
          textDecoration: 'none',
          textAlign: 'center',
        }}
      >
        <AlertCircle
          size={13}
          strokeWidth={2.2}
          style={{ color: '#C46A6A', flexShrink: 0 }}
        />
        <span>
          <strong style={{ color: '#C46A6A' }}>Free trial ended</strong>
          <span style={{ opacity: 0.55, margin: '0 6px' }}>·</span>
          {priceStr}/month
          <span style={{ opacity: 0.55, margin: '0 6px' }}>·</span>
          <span style={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}>
            Subscribe to continue
          </span>
        </span>
      </Link>
    );
  }

  return null;
};

export default TrialRibbon;
